import React, { Component } from 'react';
import { X, Eye, FileText, Download, Tag } from 'lucide-react';
import styles from './PostPreview.module.scss';

interface Post {
  id: string;
  title?: string;
  description?: string;
  contentText?: string;
  postType: string;
  attachments?: any[];
  status: 'draft' | 'published';
  viewsCount: number;
  likesCount: number;
  commentsCount: number;
  sharesCount: number;
  author?: any;
  tags?: string[];
}

interface PostPreviewProps {
  post: Post;
  onClose: () => void;
  onPublish?: () => void;
}

export class PostPreview extends Component<PostPreviewProps> {
  formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  renderAttachment = (attachment: any) => {
    switch (attachment.attachmentType) {
      case 'image':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <img
              src={attachment.fileUrl || attachment.thumbnailUrl}
              alt={attachment.fileName || 'Post image'}
              className={styles.attachmentImage}
            />
          </div>
        );
      
      case 'video':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <video
              src={attachment.fileUrl}
              controls
              className={styles.attachmentVideo}
              poster={attachment.thumbnailUrl}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      
      case 'gif':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <img
              src={attachment.fileUrl || attachment.thumbnailUrl}
              alt={attachment.fileName || 'GIF'}
              className={styles.attachmentImage}
            />
          </div>
        );
      
      case 'file':
      case 'pdf':
      case 'word':
        return (
          <div key={attachment.id} className={styles.fileAttachment}>
            <div className={styles.fileIcon}>
              <FileText size={32} />
            </div>
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{attachment.fileName || 'File'}</span>
              {attachment.fileSize && (
                <span className={styles.fileSize}>{this.formatFileSize(attachment.fileSize)}</span>
              )}
            </div>
            {attachment.fileUrl && (
              <a
                href={attachment.fileUrl}
                download
                className={styles.downloadButton}
                title="Download file"
              >
                <Download size={20} />
              </a>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  render() {
    const { post, onClose, onPublish } = this.props;

    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.previewContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.previewHeader}>
            <h2 className={styles.previewTitle}>Post Preview</h2>
            <button className={styles.closeButton} onClick={onClose} title="Close">
              <X size={24} />
            </button>
          </div>

          <div className={styles.previewBody}>
            {/* Author Info */}
            {post.author && (
              <div className={styles.authorSection}>
                <img
                  src={post.author.profilePic || '/default-avatar.png'}
                  alt={post.author.displayName}
                  className={styles.authorAvatar}
                />
                <div className={styles.authorDetails}>
                  <span className={styles.authorName}>{post.author.displayName}</span>
                  <span className={styles.postDate}>
                    {post.status === 'draft' ? 'Draft' : 'Published'}
                  </span>
                </div>
              </div>
            )}

            {/* Title */}
            {post.title && (
              <h3 className={styles.postTitle}>{post.title}</h3>
            )}

            {/* Description */}
            {post.description && (
              <p className={styles.postDescription}>{post.description}</p>
            )}

            {/* Content */}
            {post.contentText && (
              <div className={styles.postContent}>
                <p>{post.contentText}</p>
              </div>
            )}

            {/* Attachments */}
            {post.attachments && post.attachments.length > 0 && (
              <div className={styles.attachmentsContainer}>
                {post.attachments.map(attachment => this.renderAttachment(attachment))}
              </div>
            )}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className={styles.postTags}>
                <Tag size={18} className={styles.tagsIcon} />
                <div className={styles.tagsList}>
                  {post.tags.map((tag, index) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Stats Preview */}
            <div className={styles.postStats}>
              <span className={styles.statItem}>
                👁️ {post.viewsCount || 0} views
              </span>
              <span className={styles.statItem}>
                ❤️ {post.likesCount || 0} likes
              </span>
              <span className={styles.statItem}>
                💬 {post.commentsCount || 0} comments
              </span>
              <span className={styles.statItem}>
                📤 {post.sharesCount || 0} shares
              </span>
            </div>
          </div>

          <div className={styles.previewFooter}>
            {post.status === 'draft' && onPublish && (
              <button
                className={styles.publishButton}
                onClick={() => {
                  onPublish();
                  onClose();
                }}
              >
                <Eye size={18} />
                <span>Publish Now</span>
              </button>
            )}
            <button className={styles.closeFooterButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default PostPreview;

